import React, { useState } from 'react';
import { useInfiniteQuery } from 'react-query';

import { Collapse } from 'antd';
import type { CollapseProps } from 'antd';
import styled from 'styled-components';

import { getActivities } from 'apis/ActivitiesAPI';
import ActivityFeed from 'components/Activities/ActivityFeed';
import Button from 'components/lib/Button';
import { canViewShopperCommunication, isMysteryShopAdmin, isMysteryShopShopper } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import { PaginatedCommunityActivitiesType } from 'types/communities';

import EditShopRequestCommunicationLogModal from './EditShopRequestCommunicationLogModal';
import { useEditShopRequestModalContext } from './EditShopRequestModalContext';
import EditShopRequestModalNote from './EditShopRequestModalNote';
import EditShopRequestModalTranscriptRequired from './EditShopRequestModalTranscriptRequired';
import NoteModal from '../NoteModal/NoteModal';

const Container = styled.div`
  display: flex;
  flex-flow: column;
  flex-grow: 1;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-y: auto;
  padding-right: 4px;
  scrollbar-gutter: stable;
`;

const Sections = styled(Collapse)`
  width: 100%;
  background: transparent;

  &.ant-collapse {
    border: none;
  }

  .ant-collapse-item {
    border-bottom: 1px solid var(--line-gray);
  }

  .ant-collapse-item:last-child {
    border-bottom: none;
  }

  .ant-collapse-header {
    align-items: center !important;
    padding: 8px 0 !important;
  }

  .ant-collapse-content-box {
    padding-left: 0 !important;
    padding-right: 0 !important;
    padding-top: 0 !important;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  gap: 4px;
  font-size: 14px;
`;

const SectionTitle = styled.span`
  font-family: var(--font-bold);
`;

const HeaderEditButton = styled(Button)`
  color: var(--dark-blue);
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  box-shadow: none;
`;

const HeaderActionButton = styled(Button)`
  height: 26px;
  line-height: 1;
  margin-left: auto;
  margin-right: 8px;
  padding: 0 10px;
  box-shadow: none;
`;

const ActivitiesContainer = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
`;

const EDITABLE_NOTE_TYPE = {
  ADMIN: 'admin',
  SHOPPER: 'shopper',
} as const;

const SECTION_KEY = {
  FURTHER_NOTE: 'further-note',
  SHOPPER_NOTE: 'shopper-note',
  COMMUNICATION: 'activities',
} as const;

const DEFAULT_ACTIVE_SECTION_KEYS = [SECTION_KEY.FURTHER_NOTE, SECTION_KEY.SHOPPER_NOTE];

type EditableNoteType = (typeof EDITABLE_NOTE_TYPE)[keyof typeof EDITABLE_NOTE_TYPE] | null;

const EditShopRequestModalNotes = () => {
  const { shopRequest, setShopRequest } = useEditShopRequestModalContext();
  const { currentUser } = useAppSelector((state) => state.auth);
  const [editableNote, setEditableNote] = useState<EditableNoteType>(null);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const isShopRequestAdmin = isMysteryShopAdmin(currentUser);
  const isShopRequestShopper = isMysteryShopShopper(currentUser);
  const showShopperCommunication = canViewShopperCommunication(currentUser);

  const showEditNoteFromShopper = isShopRequestShopper || isShopRequestAdmin;
  const disableEditNoteFromShopper =
    !isShopRequestAdmin && isShopRequestShopper && currentUser?.PAUser_Id !== shopRequest?.shopper?.userId;
  const showTranscriptRequirement = showShopperCommunication;
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isLoading, refetch } =
    useInfiniteQuery<PaginatedCommunityActivitiesType>(
      ['shop-request', 'community', 'activities', shopRequest?.community?.id, shopRequest?.id],
      ({ pageParam = null }) =>
        getActivities(shopRequest?.community?.id as number, {
          nextPageUrl: pageParam,
          shop_request_id: shopRequest?.id,
        }),
      {
        enabled: Boolean(shopRequest?.community?.id && shopRequest?.id && showShopperCommunication),
        refetchOnWindowFocus: false,
        getNextPageParam: (lastPage) => lastPage.next || undefined,
      }
    );
  const activities = data?.pages.flatMap((page) => page.results) ?? [];

  const openNoteEditor = (noteType: Exclude<EditableNoteType, null>) => (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setEditableNote(noteType);
  };

  const handleAddLogClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setIsLogModalOpen(true);
  };

  const closeNoteEditor = () => setEditableNote(null);

  const items: NonNullable<CollapseProps['items']> = [
    {
      key: SECTION_KEY.FURTHER_NOTE,
      label: (
        <SectionHeader>
          <SectionTitle>Note from Further</SectionTitle>
          {isShopRequestAdmin && (
            <>
              <span>·</span>
              <HeaderEditButton size="small" type="link" onClick={openNoteEditor(EDITABLE_NOTE_TYPE.ADMIN)}>
                Edit
              </HeaderEditButton>
            </>
          )}
        </SectionHeader>
      ),
      children: (
        <EditShopRequestModalNote
          title="Note from Further"
          note={shopRequest?.adminNote}
          allowEdit={false}
          hideTitle
          updateNote={(note: string) => setShopRequest({ ...shopRequest!, adminNote: note })}
        />
      ),
    },
    {
      key: SECTION_KEY.SHOPPER_NOTE,
      label: (
        <SectionHeader>
          <SectionTitle>Note from Shopper</SectionTitle>
          {showEditNoteFromShopper && (
            <>
              <span>·</span>
              <HeaderEditButton
                size="small"
                type="link"
                disabled={disableEditNoteFromShopper}
                onClick={openNoteEditor(EDITABLE_NOTE_TYPE.SHOPPER)}
              >
                Edit
              </HeaderEditButton>
            </>
          )}
        </SectionHeader>
      ),
      children: (
        <EditShopRequestModalNote
          title="Note from Shopper"
          note={shopRequest?.shopperNote}
          allowEdit={false}
          hideTitle
          updateNote={(note: string) => setShopRequest({ ...shopRequest!, shopperNote: note })}
        />
      ),
    },
  ];

  if (showShopperCommunication) {
    items.push({
      key: SECTION_KEY.COMMUNICATION,
      label: (
        <SectionHeader>
          <SectionTitle>Communication</SectionTitle>
          <HeaderActionButton size="small" type="primary" onClick={handleAddLogClick}>
            + Add Log
          </HeaderActionButton>
        </SectionHeader>
      ),
      children: (
        <ActivitiesContainer>
          <ActivityFeed
            activities={activities}
            communityId={shopRequest?.community?.id}
            hasNextPage={hasNextPage}
            isFetching={isFetching}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={isLoading}
            emptyText="No activities have been recorded for this community yet."
            onLoadMore={() => fetchNextPage()}
            onActivityUpdated={() => refetch()}
          />
        </ActivitiesContainer>
      ),
    });
  }

  return (
    <Container className={'mini-scroll'}>
      {showTranscriptRequirement && <EditShopRequestModalTranscriptRequired />}
      <Sections ghost defaultActiveKey={DEFAULT_ACTIVE_SECTION_KEYS} items={items} />
      {editableNote === EDITABLE_NOTE_TYPE.ADMIN && (
        <NoteModal
          title="Edit Note"
          note={shopRequest?.adminNote}
          isModalOpen
          closeModal={closeNoteEditor}
          modifyNote={(note: string) => setShopRequest({ ...shopRequest!, adminNote: note })}
        />
      )}
      {editableNote === EDITABLE_NOTE_TYPE.SHOPPER && (
        <NoteModal
          title="Edit Note"
          note={shopRequest?.shopperNote}
          isModalOpen
          closeModal={closeNoteEditor}
          modifyNote={(note: string) => setShopRequest({ ...shopRequest!, shopperNote: note })}
        />
      )}
      <EditShopRequestCommunicationLogModal
        communityId={shopRequest?.community?.id}
        shopRequestId={shopRequest?.id}
        requestType={shopRequest?.request_type}
        isOpen={isLogModalOpen}
        onCancel={() => setIsLogModalOpen(false)}
        onSaveSuccess={() => refetch()}
      />
    </Container>
  );
};

export default EditShopRequestModalNotes;
